import { useState } from "react";
import { Database, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-app-shell grid lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden lg:flex flex-col justify-between p-12 border-r grid-bg overflow-hidden">
        <div className="flex items-center gap-3 fade-up">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center"><Database size={18} /></div>
          <span className="font-display text-xl font-bold tracking-tight">AccessBridge</span>
        </div>
        <div className="max-w-lg fade-up" style={{ animationDelay: "120ms" }}>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-5">.mdb / .accdb / .wdb → web</p>
          <h1 className="text-5xl font-extrabold tracking-tight leading-[1.05]">
            Your old Access database,<br />now a modern web app.
          </h1>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Upload any Microsoft Access or Microsoft Works database. Every table becomes a searchable, editable view you can export to CSV, Excel or JSON.
          </p>
        </div>
        <div className="font-mono text-xs text-muted-foreground flex gap-8 fade-up" style={{ animationDelay: "240ms" }}>
          <span>01 · Upload</span><span>02 · Browse & edit</span><span>03 · Export</span>
        </div>
      </section>

      <section className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-2">Only you can access your converted databases.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" data-testid="login-email-input" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input id="password" type={show ? "text" : "password"} required value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11 pr-10" />
              <button type="button" onClick={() => setShow(!show)} data-testid="login-toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full h-11 hover:bg-blue-600 hover:text-white" data-testid="login-submit-button">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <>Sign in <ArrowRight size={16} /></>}
          </Button>
          <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
          <Button type="button" variant="outline" onClick={googleSignIn} className="w-full h-11 hover:bg-secondary hover:text-foreground" data-testid="google-signin-button">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.9-5.5 3.9-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.3 14.6 2.3 12 2.3 6.7 2.3 2.4 6.6 2.4 12s4.3 9.7 9.6 9.7c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z"/></svg>
            Continue with Google
          </Button>
          <p className="text-xs text-muted-foreground text-center">Techs: use the email the admin added for you.</p>
        </form>
      </section>
    </div>
  );
}
